import { useMemo } from "react";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { DOC_BLOCK_TYPE_LABELS } from "@/lib/document-builder/blocks/blockTypes";
import { sortDocBlocks } from "@/lib/document-builder/blocks/blockReorder";
import {
  evaluateDocFormulaFields,
  type DocFormulaEvaluation,
} from "@/lib/document-builder/formulas/docFormulaEval";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { applyVariableValuesToBlocks } from "@/lib/document-builder/variables/variableSubstitution";
import { DocumentBlockPreview } from "./DocumentBlockPreview";

type Props = {
  blocks: DocBlock[];
  assets?: DocAsset[];
  variableValues?: DocVariableValues | null;
};

export function DocumentBlockReadOnlyList({ blocks, assets = [], variableValues }: Props) {
  const formulaEvaluation = useMemo(
    () => (variableValues ? evaluateDocFormulaFields({ blocks, variableValues }) : null),
    [blocks, variableValues],
  );

  const resolvedBlocks = variableValues
    ? applyVariableValuesToBlocks(blocks, variableValues)
    : blocks;
  const sorted = sortDocBlocks(resolvedBlocks);

  if (sorted.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">This version has no blocks.</p>;
  }

  return (
    <div className="space-y-3">
      {sorted.map((block) => (
        <BlockArticle
          key={block.id}
          block={block}
          assets={assets}
          variableValues={variableValues}
          formulaEvaluation={formulaEvaluation}
        />
      ))}
    </div>
  );
}

function BlockArticle({
  block,
  assets,
  variableValues,
  formulaEvaluation,
}: {
  block: DocBlock;
  assets: DocAsset[];
  variableValues?: DocVariableValues | null;
  formulaEvaluation: DocFormulaEvaluation | null;
}) {
  if (block.type === "component_ref") {
    return (
      <article className="rounded-lg border border-dashed border-amber-200 bg-amber-50/50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
        <DocumentBlockPreview
          block={block}
          assets={assets}
          variableValues={variableValues}
          formulaEvaluation={formulaEvaluation}
        />
      </article>
    );
  }

  return (
    <article className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">
        {DOC_BLOCK_TYPE_LABELS[block.type]}
      </p>
      <DocumentBlockPreview
        block={block}
        assets={assets}
        variableValues={variableValues}
        formulaEvaluation={formulaEvaluation}
      />
    </article>
  );
}
